import { Dispatch, ReactNode, SetStateAction } from 'react';

import { LunchFundsLoadingValues } from '@epi-models/pages/Home';

export interface IBenefitTile {
  type: string;
  limit: number;
}

export interface IBenefitTileCommon {
  icon: ReactNode;
  title: string;
  limit: number;
}

export interface IBenefitGroupSettings {
  benefitType: string;
  maxAmountPerPeriod: number;
}

export interface IBenefitGroupDetailSettings extends IBenefitGroupSettings {
  recurringTopupRequestEnabled: boolean;
}

export interface IBenefitGroup {
  beneficiaryCount: number;
  groupId: number;
  groupName: string;
  benefitSettings: IBenefitGroupSettings[];
}

export interface IBenefitGroupDetails {
  groupId: number;
  groupName: string;
  benefitSettings: IBenefitGroupDetailSettings[];
}

export interface IBenefitGroupTile {
  benefitGroup: IBenefitGroup;
  newGroupId?: number;
  isLastGroup?: boolean;
  isProcessing?: boolean;
}

export interface IBenefitGroupDictionary {
  beneficiaryCount: number;
  groupId: number;
  groupName: string;
  benefitSettings: Record<IBenefits, { maxAmountPerPeriod: number }>;
}

export interface BenefitGroupsResponse {
  items: IBenefitGroup[];
  currentPage: number;
  totalPages: number;
  rowsPerPage: number;
  totalCount: number;
}

export interface ILunchGlobalSettings {
  maxAmountPerPeriod: number;
  isMaximumSpendingLimit: boolean;
  recurringTopupRequestEnabled: boolean;
  fundsLoadingType: LunchFundsLoadingValues;
  isPendingScheduledLoadsForBenefit: boolean;
  isFundsLoadingTypePending: boolean;
  canApplyFundsLoadingChangeImmediately: boolean;
}

export type MyChoiceSettings = MyChoiceSettingsPayload['myChoiceSettings'];

export const DEFAULT_MY_CHOICE_SETTINGS: MyChoiceSettings = {
  Lunch: false,
  Recreational: false,
  Transport: false,
  Massage: false,
  Wellbeing: false
};

export const BENEFIT_TO_MY_CHOICE_KEY: Record<
  IBenefits,
  keyof MyChoiceSettings
> = {
  lunch: 'Lunch',
  recreational: 'Recreational',
  transport: 'Transport',
  massage: 'Massage',
  wellbeing: 'Wellbeing'
};

export interface IBenefitDefaultSettingsResponse {
  contractedBenefits: Record<
    IBenefits,
    {
      minAmount: number;
      maxAmount: number;
      taxFreeAmount?: number;
      myChoiceEnabled: boolean;
    }
  > | null;
  lunchGlobalSettings: ILunchGlobalSettings | null;
  companyMyChoiceEnabled: boolean;
}

export interface ContractedBenefitsSettings {
  benefitType: IBenefits;
  minAmount: number;
  maxAmount: number;
  taxFreeAmount?: number;
  myChoiceEnabled: boolean;
}

export interface IBenefitDefaultSettings {
  contractedBenefits: ContractedBenefitsSettings[];
  lunchGlobalSettings: ILunchGlobalSettings | null;
  companyMyChoiceEnabled: boolean;
}

export type IBenefitSettings = Record<
  IBenefits,
  | {
      maxAmountPerPeriod?: number;
      isMaximumSpendingLimit?: boolean;
      fundsLoadingType?: LunchFundsLoadingValues;
      recurringTopupRequestEnabled?: boolean;
    }
  | undefined
>;

export interface ICreateBenefitGroupForm {
  groupName: string;
  benefitSettings: IBenefitSettings;
}

export interface ICreateBenefitGroupFormProps {
  defaultSettings: ICreateBenefitGroupForm;
  toggleModal: Dispatch<SetStateAction<boolean>>;
}

export interface DialogCreateAnotherGroupProps {
  openModal: boolean;
  toggleModal: Dispatch<SetStateAction<boolean>>;
  defaultSettings: ICreateBenefitGroupForm;
}

export interface IBenefitGroup {
  benefitType: string;
  groupId: number;
  groupName: string;
  maxAmountPerPeriod: number;
  fundsLoadingType?: string;
  recurringTopupRequestEnabled: boolean;
  isMaximumSpendingLimit: boolean;
  isFundsLoadingTypePending?: boolean;
  isPendingScheduledLoadsForBenefit?: boolean;
  canApplyFundsLoadingChangeImmediately: boolean;
}

export interface IDailyLimitOptions {
  display: string;
  id: number;
  upToDate: boolean;
}

export interface IDailyLimitField {
  readOnly?: boolean;
  label?: string;
  benefitType: string;
  helperText?: string;
  disabled?: boolean;
  options?: IDailyLimitOptions[];
  name: string;
  fundsLoadingTypeName: string;
  maxAmounts: number[];
  currentMaxAmount?: number;
}

export interface IBenefitInformationalModalType {
  showInformationModal: boolean;
  handleCloseModal: (isOpen: boolean) => void;
}

export interface IBenefitMaxAmounts {
  Lunch: number;
  Recreational: number;
  Massage: number;
  Wellbeing: number;
  Transport: number;
}

export interface IBenefitTypeIcon {
  benefit: string;
  color?: string;
}

export interface IAvailableBenefits {
  id: number;
  type: string;
  state: string;
}

export interface IBenefitType {
  Lunch: IBenefits;
  Transport: IBenefits;
  Massage: IBenefits;
  Wellbeing: IBenefits;
  Recreational: IBenefits;
}

export type IBenefits =
  | 'lunch'
  | 'transport'
  | 'massage'
  | 'wellbeing'
  | 'recreational';

export type DeleteGroupModalContentType =
  | 'confirm'
  | 'not_empty'
  | 'last_group'
  | 'in_progress';

export interface DeleteGroupModalProps {
  show: boolean;
  onClose: () => void;
  groupId: number;
  groupName: string;
  beneficiaryCount: number;
  isLastGroup?: boolean;
  isProcessing?: boolean;
}

export interface MyChoiceSettingsPayload {
  myChoiceSettings: {
    Lunch: boolean;
    Recreational: boolean;
    Transport: boolean;
    Massage: boolean;
    Wellbeing: boolean;
  };
}
